import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { CmsPage, CmsPageSummary, ContentBlock } from '@/types/cms';

// ── List all CMS pages (admin) ──

export function useAdminPagesList() {
  const [pages, setPages] = useState<CmsPageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_list_cms_pages');
      if (rpcError) throw rpcError;
      setPages((data as CmsPageSummary[]) || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch pages');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { pages, isLoading, error, refetch: fetch };
}

// ── Fetch single CMS page for editing (admin) ──

export function useAdminPage(id: string | undefined) {
  const [page, setPage] = useState<CmsPage | null>(null);
  const [isLoading, setIsLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('cms_pages')
        .select('*')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;
      setPage(data as CmsPage);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch page');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { page, isLoading, error, refetch: fetch };
}

// ── Create a new CMS page ──

export async function createCmsPage(page: {
  slug: string;
  template: string;
  title_en: string;
  title_fr: string;
  subtitle_en?: string;
  subtitle_fr?: string;
  content_en?: ContentBlock[];
  content_fr?: ContentBlock[];
  status?: string;
}): Promise<CmsPage> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const { data, error } = await supabase
    .from('cms_pages')
    .insert({
      slug: page.slug,
      template: page.template,
      title_en: page.title_en,
      title_fr: page.title_fr,
      subtitle_en: page.subtitle_en || '',
      subtitle_fr: page.subtitle_fr || '',
      content_en: page.content_en || [],
      content_fr: page.content_fr || [],
      status: page.status || 'draft',
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data as CmsPage;
}

// ── Update a CMS page ──

export async function updateCmsPage(
  id: string,
  updates: Partial<Omit<CmsPage, 'id' | 'created_at' | 'created_by'>>
): Promise<CmsPage> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const { data, error } = await supabase
    .from('cms_pages')
    .update({ ...updates, updated_by: userId })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CmsPage;
}

// ── Publish / Unpublish ──

export async function publishCmsPage(id: string): Promise<void> {
  const { error } = await supabase
    .from('cms_pages')
    .update({ status: 'published', published_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function unpublishCmsPage(id: string): Promise<void> {
  const { error } = await supabase
    .from('cms_pages')
    .update({ status: 'draft', published_at: null })
    .eq('id', id);
  if (error) throw error;
}

// ── Delete a CMS page ──

export async function deleteCmsPage(id: string): Promise<void> {
  const { error } = await supabase.from('cms_pages').delete().eq('id', id);
  if (error) throw error;
}

// ── Check slug availability ──

export async function checkSlugAvailable(slug: string, excludeId?: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_cms_slug_available', {
    p_slug: slug,
    p_exclude_id: excludeId || null,
  });
  if (error) return false;
  return data as boolean;
}

// ── Fetch published page by slug (public) ──

export async function fetchPublishedPage(slug: string): Promise<CmsPage | null> {
  const { data, error } = await supabase
    .from('cms_pages')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .single();

  if (error || !data) return null;
  return data as CmsPage;
}
